import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Plus, Trash2, Edit3, X, FileText, ShoppingBag, Rocket, Construction, Mail, Download, KeyRound } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Header } from "@/components/Header";
import { toast } from "sonner";

const CATEGORIES = ["People to Know", "Blogs", "Podcasts", "Congressional Hearings", "Conspiracies"];
const TYPES = ["article", "story", "video"];

const emptyPost = {
  title: "", summary: "", url: "", source: "", category: "People to Know",
  content_type: "article", image_url: "", youtube_id: "", read_time: "5 MIN READ", featured: false,
};

const PostForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial || emptyPost);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      if (form.id) await api.put(`/posts/${form.id}`, form);
      else await api.post("/posts", form);
      toast.success(form.id ? "Dispatch updated" : "Dispatch published");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full bg-paper border border-rule px-3 py-2 font-body text-sm text-ink focus:outline-none focus:border-signal";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="post-form" className="bg-surface max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-2xl font-bold text-ink">{form.id ? "Edit" : "New"} Dispatch</h2>
          <button onClick={onClose} className="text-muted-ink hover:text-signal"><X size={22} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="overline-label text-muted-ink block mb-1">Title</label>
            <input data-testid="post-title" className={field} value={form.title} onChange={(e) => set("title", e.target.value)} />
          </div>
          <div>
            <label className="overline-label text-muted-ink block mb-1">Summary</label>
            <textarea data-testid="post-summary" rows={3} className={field} value={form.summary} onChange={(e) => set("summary", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="overline-label text-muted-ink block mb-1">Source Outlet</label>
              <input data-testid="post-source" className={field} value={form.source} onChange={(e) => set("source", e.target.value)} placeholder="THE DEBRIEF" />
            </div>
            <div>
              <label className="overline-label text-muted-ink block mb-1">External URL</label>
              <input data-testid="post-url" className={field} value={form.url} onChange={(e) => set("url", e.target.value)} placeholder="https://" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="overline-label text-muted-ink block mb-1">Category</label>
              <select data-testid="post-category" className={field} value={form.category} onChange={(e) => set("category", e.target.value)}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="overline-label text-muted-ink block mb-1">Type</label>
              <select data-testid="post-type" className={field} value={form.content_type} onChange={(e) => set("content_type", e.target.value)}>
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="overline-label text-muted-ink block mb-1">Image URL</label>
            <input data-testid="post-image" className={field} value={form.image_url} onChange={(e) => set("image_url", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="overline-label text-muted-ink block mb-1">YouTube ID (video only)</label>
              <input data-testid="post-youtube" className={field} value={form.youtube_id} onChange={(e) => set("youtube_id", e.target.value)} placeholder="dQw4w9WgXcQ" />
            </div>
            <div>
              <label className="overline-label text-muted-ink block mb-1">Read / Watch Time</label>
              <input data-testid="post-readtime" className={field} value={form.read_time} onChange={(e) => set("read_time", e.target.value)} />
            </div>
          </div>
          <label className="flex items-center gap-2 font-mono2 text-xs text-ink">
            <input data-testid="post-featured" type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} />
            Set as featured lead story
          </label>
        </div>
        <button data-testid="post-save" onClick={save} disabled={busy} className="mt-6 w-full bg-signal text-white font-mono2 text-xs uppercase tracking-[0.2em] py-3 hover:opacity-90 disabled:opacity-50">
          {busy ? "Saving…" : "Publish Dispatch"}
        </button>
      </div>
    </div>
  );
};

const ChangePassword = () => {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const field = "w-full bg-paper border border-rule px-3 py-2 font-mono2 text-sm text-ink focus:outline-none focus:border-signal";

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    if (form.new_password.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success(data.message || "Password updated");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="account-panel" className="max-w-md">
      <div className="bg-surface border border-rule p-6">
        <div className="overline-label text-signal mb-2">// Security</div>
        <h2 className="font-display text-2xl font-bold text-ink">Change Password</h2>
        <p className="font-body text-muted-ink text-sm mt-1 mb-5">Update your editor login password. You'll stay signed in.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="overline-label text-muted-ink block mb-1">Current Password</label>
            <input data-testid="cp-current" type="password" className={field} value={form.current_password} onChange={(e) => set("current_password", e.target.value)} required />
          </div>
          <div>
            <label className="overline-label text-muted-ink block mb-1">New Password</label>
            <input data-testid="cp-new" type="password" className={field} value={form.new_password} onChange={(e) => set("new_password", e.target.value)} placeholder="min. 8 characters" required />
          </div>
          <div>
            <label className="overline-label text-muted-ink block mb-1">Confirm New Password</label>
            <input data-testid="cp-confirm" type="password" className={field} value={form.confirm} onChange={(e) => set("confirm", e.target.value)} required />
          </div>
          <button data-testid="cp-submit" type="submit" disabled={busy} className="w-full bg-signal text-white font-mono2 text-xs uppercase tracking-[0.2em] py-3 hover:opacity-90 disabled:opacity-50">
            {busy ? "Updating…" : "Update Password"}
          </button>
        </form>
      </div>
    </div>
  );
};

const Admin = () => {
  const { user, ready } = useAuth();
  const { comingSoon, setComingSoon } = useSettings();
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [products, setProducts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [editing, setEditing] = useState(null);

  const loadPosts = () => api.get("/posts").then(({ data }) => setPosts(data));
  const loadProducts = () => api.get("/products").then(({ data }) => setProducts(data));
  const loadSubscribers = () => api.get("/subscribers").then(({ data }) => setSubscribers(data));

  useEffect(() => {
    if (user) {
      loadPosts();
      loadProducts();
      loadSubscribers();
    }
  }, [user]);

  if (ready && !user) return <Navigate to="/login" replace />;

  const delPost = async (id) => {
    await api.delete(`/posts/${id}`);
    toast.success("Dispatch removed");
    loadPosts();
  };

  return (
    <div className="bg-paper min-h-screen">
      <Header activeCategory={null} onCategory={() => {}} />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between border-b-2 border-ink pb-4 mb-8 gap-4">
          <div>
            <div className="overline-label text-signal mb-2">// Editor Desk — {user?.email}</div>
            <h1 className="font-display text-4xl font-black text-ink tracking-tight">Newsroom Control</h1>
          </div>
          <div className="flex gap-2">
            <button data-testid="tab-posts" onClick={() => setTab("posts")} className={`font-mono2 text-xs uppercase tracking-[0.15em] px-4 py-2 border flex items-center gap-2 ${tab === "posts" ? "bg-ink text-paper border-ink" : "border-rule text-ink"}`}>
              <FileText size={14} /> Dispatches ({posts.length})
            </button>
            <button data-testid="tab-store" onClick={() => setTab("store")} className={`font-mono2 text-xs uppercase tracking-[0.15em] px-4 py-2 border flex items-center gap-2 ${tab === "store" ? "bg-ink text-paper border-ink" : "border-rule text-ink"}`}>
              <ShoppingBag size={14} /> Store ({products.length})
            </button>
            <button data-testid="tab-subscribers" onClick={() => setTab("subscribers")} className={`font-mono2 text-xs uppercase tracking-[0.15em] px-4 py-2 border flex items-center gap-2 ${tab === "subscribers" ? "bg-ink text-paper border-ink" : "border-rule text-ink"}`}>
              <Mail size={14} /> Subscribers ({subscribers.length})
            </button>
            <button data-testid="tab-account" onClick={() => setTab("account")} className={`font-mono2 text-xs uppercase tracking-[0.15em] px-4 py-2 border flex items-center gap-2 ${tab === "account" ? "bg-ink text-paper border-ink" : "border-rule text-ink"}`}>
              <KeyRound size={14} /> Account
            </button>
          </div>
        </div>

        {/* Site status control */}
        <div data-testid="site-status-banner" className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 border p-5 mb-8 ${comingSoon ? "border-signal bg-signal/5" : "border-cyan bg-cyan/5"}`}>
          <div className="flex items-start gap-3">
            {comingSoon ? <Construction className="text-signal mt-0.5" size={22} /> : <Rocket className="text-cyan mt-0.5" size={22} />}
            <div>
              <div className="font-grotesk font-semibold text-sm text-ink">
                {comingSoon ? "Coming Soon mode is ON" : "Site is LIVE"}
              </div>
              <p className="font-body text-sm text-muted-ink mt-0.5">
                {comingSoon
                  ? "Visitors see the holding page. You can still browse and build the archive while logged in."
                  : "The full journal and store are visible to everyone."}
              </p>
            </div>
          </div>
          <button
            data-testid="toggle-coming-soon"
            onClick={async () => {
              const next = !comingSoon;
              try {
                await setComingSoon(next);
                toast.success(next ? "Coming Soon mode enabled" : "Site is now live");
              } catch (e) {
                toast.error("Could not update site status. Please try again.");
              }
            }}
            className={`font-mono2 text-xs uppercase tracking-[0.2em] px-5 py-3 whitespace-nowrap hover:opacity-90 ${comingSoon ? "bg-cyan text-ink" : "bg-signal text-white"}`}
          >
            {comingSoon ? "Go Live Now" : "Switch to Coming Soon"}
          </button>
        </div>

        {tab === "posts" && (
          <>
            <button data-testid="new-post" onClick={() => setEditing(emptyPost)} className="mb-6 bg-signal text-white font-mono2 text-xs uppercase tracking-[0.2em] px-5 py-3 flex items-center gap-2 hover:opacity-90">
              <Plus size={16} /> New Dispatch
            </button>
            <div className="space-y-3">
              {posts.map((p) => (
                <div key={p.id} data-testid={`admin-post-${p.id}`} className="bg-surface border border-rule p-4 flex items-center gap-4">
                  <img src={p.image_url} alt="" className="w-16 h-16 object-cover hidden sm:block" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono2 text-[0.6rem] bg-ink text-paper px-1.5 py-0.5 uppercase">{p.source}</span>
                      <span className="overline-label text-muted-ink">{p.category} · {p.content_type}</span>
                      {p.featured && <span className="font-mono2 text-[0.6rem] text-signal">★ LEAD</span>}
                    </div>
                    <h3 className="font-grotesk font-semibold text-sm text-ink truncate">{p.title}</h3>
                  </div>
                  <button data-testid={`edit-post-${p.id}`} onClick={() => setEditing(p)} className="p-2 text-muted-ink hover:text-cyan"><Edit3 size={16} /></button>
                  <button data-testid={`delete-post-${p.id}`} onClick={() => delPost(p.id)} className="p-2 text-muted-ink hover:text-signal"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "store" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((p) => (
              <div key={p.id} className="bg-surface border border-rule p-4 flex gap-4">
                <img src={p.image_url} alt="" className="w-20 h-20 object-cover" />
                <div>
                  <span className="overline-label text-muted-ink">{p.category}</span>
                  <h3 className="font-grotesk font-semibold text-sm text-ink leading-snug">{p.name}</h3>
                  <span className="font-mono2 text-sm text-ink">${p.price.toFixed(2)}</span>
                </div>
              </div>
            ))}
            <p className="col-span-full font-body text-muted-ink italic text-sm">Products are pre-seeded for this demo store.</p>
          </div>
        )}

        {tab === "subscribers" && (
          <div data-testid="subscribers-panel">
            <div className="flex items-center justify-between mb-5">
              <p className="font-body text-muted-ink text-sm">
                {subscribers.length === 0
                  ? "No subscribers yet. Signups from the site and Coming Soon page will appear here."
                  : `${subscribers.length} ${subscribers.length === 1 ? "person has" : "people have"} joined your list.`}
              </p>
              {subscribers.length > 0 && (
                <button
                  data-testid="export-subscribers"
                  onClick={() => {
                    const rows = ["email,subscribed_at", ...subscribers.map((s) => `${s.email},${s.created_at || ""}`)];
                    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "disclosurejournal-subscribers.csv";
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="font-mono2 text-[0.65rem] uppercase tracking-[0.2em] px-4 py-2 border border-rule text-ink hover:border-ink flex items-center gap-2"
                >
                  <Download size={13} /> Export CSV
                </button>
              )}
            </div>
            <div className="bg-surface border border-rule divide-y divide-[var(--rule)]">
              {subscribers.map((s, i) => (
                <div key={s.email} data-testid={`subscriber-${i}`} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Mail size={14} className="text-signal shrink-0" />
                    <span className="font-grotesk text-sm text-ink truncate">{s.email}</span>
                  </div>
                  <span className="font-mono2 text-[0.65rem] text-muted-ink tracking-wider whitespace-nowrap ml-4">
                    {s.created_at ? new Date(s.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "account" && <ChangePassword />}
      </main>

      {editing && <PostForm initial={editing} onClose={() => setEditing(null)} onSaved={loadPosts} />}
    </div>
  );
};

export default Admin;
